import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';

import { logArticleVideoPlay } from '../../../bi';
import { selectArticleState } from '../../../common/selectors/helpCenter';

declare global {
  interface Window {
    YT: any;
  }
}

function extractVideoIdFromUrl(url: string | null): string | null {
  if (!url) {
    return null;
  }

  const videoIdPattern =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|watch)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\r\n]{11})/;

  const match = url.match(videoIdPattern);
  return match ? match[1] : null;
}

export function useYouTubeVideoTracker(containerRef: RefObject<HTMLDivElement>, articleId: string): void {
  const store = useStore();
  const { isLoading } = useSelector(selectArticleState);
  const playersRef = useRef<any[]>([]);

  const handleVideoPlay = useCallback(
    (videoUrl: string) => {
      const state = store.getState();
      logArticleVideoPlay(state, { articleId, videoUrl });
    },
    [articleId, store],
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const containerElement = containerRef.current;

    if (!containerElement) {
      return;
    }

    const videoIframes = containerElement.querySelectorAll('.component.video-component iframe');

    if (videoIframes.length === 0) {
      return;
    }

    const videoData: { id: string; src: string }[] = [];

    videoIframes.forEach((iframe, index) => {
      const src = iframe.getAttribute('src');
      if (src) {
        const videoId = extractVideoIdFromUrl(src);
        if (videoId) {
          const uniqueId = `yt-video-${videoId}-${index}`;
          iframe.setAttribute('id', uniqueId);
          videoData.push({ id: uniqueId, src });
        }
      }
    });

    if (videoData.length === 0) {
      return;
    }

    const loadYouTubeAPI = () => {
      if (document.getElementById('youtube-api-script')) {
        return;
      }

      const tag = document.createElement('script');
      tag.id = 'youtube-api-script';
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }

      tag.onload = () => {
        if (window.YT) {
          window.YT.ready(() => setupVideoTracking());
        }
      };
    };

    const setupVideoTracking = () => {
      videoData.forEach(({ id, src }) => {
        if (window.YT) {
          const player = new window.YT.Player(id, {
            events: {
              onStateChange: (event: any) => {
                if (event.data === 1) {
                  handleVideoPlay(src);
                }
              },
            },
          });
          playersRef.current.push(player);
        }
      });
    };

    if (window.YT) {
      window.YT.ready(() => setupVideoTracking());
    } else {
      loadYouTubeAPI();
    }

    return () => {
      playersRef.current.forEach((player) => {
        if (player && typeof player.destroy === 'function') {
          player.destroy();
        }
      });
      playersRef.current = [];
    };
  }, [containerRef, articleId, handleVideoPlay, isLoading]);
}
